import React, { useMemo, useState } from 'react';
import { Pagination, Tooltip } from '@carbon/react';
import { Information } from '@carbon/icons-react';
import { defineMessages, useIntl } from 'react-intl';
import { createColumnHelper } from '@tanstack/react-table';

import styles from './CurrentTeamMembers.module.scss';

import TeamMemberAppRole from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamMemberAppRole/TeamMemberAppRole';
import TeamInteractions from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/TeamInteractions';
import { TeamMemberDto } from 'api/dtos/teamMember';
import TeamMemberTags from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamMemberTags/TeamMemberTags';
import ConfigurableTanstackTable from 'Components/Common/ConfigurableTanstackTable/ConfigurableTanstackTable';
import TableSearchInput from 'Components/Common/TableSearchInput/TableSearchInput';
import { SpecificTeamMemberModal } from 'Screens/Dashboard/Team/Components/TeamCellHandler/TeamInteractions/Interactions/Details/SpecificTeamMemberView/SpecificTeamMemberView';

const columnHelper = createColumnHelper<TeamMemberDto>();

const HeaderWithTooltip = ({
    label,
    tooltip,
}: {
    label: string;
    tooltip: string;
}) => (
    <span className={styles.headerWithTooltip}>
        {label}
        <Tooltip label={tooltip} align="bottom">
            <Information size={16} />
        </Tooltip>
    </span>
);

const getColumns = (intl: ReturnType<typeof useIntl>, onViewDetails: (id: string) => void) => [
    columnHelper.display({
        id: 'name',
        header: () => intl.formatMessage(messages.name),
        cell: ({ row }) => (
            <div className={styles.name}>
                {`${row.original.firstName} ${row.original.lastName}`}
            </div>
        ),
    }),
    columnHelper.accessor('email', {
        id: 'email',
        header: () => intl.formatMessage(messages.email),
        cell: ({ getValue }) => getValue(),
    }),
    columnHelper.display({
        id: 'roleInApp',
        header: () => (
            <HeaderWithTooltip
                label={intl.formatMessage(messages.roleInApp)}
                tooltip={intl.formatMessage(messages.roleInAppTooltip)}
            />
        ),
        cell: ({ row }) => <TeamMemberAppRole id={row.original.id} role={row.original.role} />,
    }),
    columnHelper.display({
        id: 'role',
        header: () => (
            <HeaderWithTooltip
                label={intl.formatMessage(messages.parishFunction)}
                tooltip={intl.formatMessage(messages.parishFunctionTooltip)}
            />
        ),
        cell: ({ row }) => <TeamMemberTags tags={row.original.tags} />,
    }),
    columnHelper.display({
        id: 'interactions',
        header: () => intl.formatMessage(messages.interactions),
        cell: ({ row }) => (
            <TeamInteractions
                id={row.original.id}
                role={row.original.role}
                enabled={row.original.enabled}
                isActive={row.original.isActive}
                canTransferOwnership={row.original.canTransferOwnership}
                onViewDetails={onViewDetails}
            />
        ),
    }),
];

type Props = {
    members: TeamMemberDto[];
    search: string;
    page: number;
    pageSize: number;
    totalItems: number;
    onSearchChange: (value: string) => void;
    onPageChange: (value: { page: number; pageSize: number }) => void;
};

const TeamMembersTable = ({
    members,
    search,
    page,
    pageSize,
    totalItems,
    onSearchChange,
    onPageChange,
}: Props) => {
    const intl = useIntl();
    const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
    const columns = useMemo(() => getColumns(intl, setSelectedMemberId), [intl]);
    const hasMembers = members.length > 0;

    return (
        <>
            <div className={styles.container}>
                {hasMembers && (
                    <TableSearchInput
                        id="search-team-members-table"
                        placeholder={intl.formatMessage(messages.searchPlaceholder)}
                        value={search}
                        onChange={onSearchChange}
                    />
                )}
                <div className={styles.tableWrapper}>
                    <ConfigurableTanstackTable
                        data={members}
                        columns={columns}
                        getRowId={(row) => row.id}
                        emptyStateTitle={intl.formatMessage(messages.emptyState)}
                    />
                </div>
                {totalItems > 0 && (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        pageSizes={[10, 25, 50]}
                        totalItems={totalItems}
                        onChange={({ page: nextPage, pageSize: nextPageSize }) => {
                            onPageChange({ page: nextPage, pageSize: nextPageSize });
                        }}
                        itemsPerPageText={intl.formatMessage(messages.itemsPerPage)}
                        backwardText={intl.formatMessage(messages.previousPage)}
                        forwardText={intl.formatMessage(messages.nextPage)}
                        itemRangeText={(min, max, total) => intl.formatMessage(messages.itemRange, { min, max, total })}
                        className={styles.pagination}
                    />
                )}
            </div>
            {selectedMemberId && (
                <SpecificTeamMemberModal
                    memberId={selectedMemberId}
                    open
                    onClose={() => { setSelectedMemberId(null); }}
                />
            )}
        </>
    );
};

const messages = defineMessages({
    name: {
        id: 'src.Screens.Dashboard.TeamMembersTable.name',
        defaultMessage: 'Imię i nazwisko',
    },
    email: {
        id: 'src.Screens.Dashboard.TeamMembersTable.email',
        defaultMessage: 'Email',
    },
    roleInApp: {
        id: 'src.Screens.Dashboard.TeamMembersTable.roleInApp',
        defaultMessage: 'Rola w aplikacji',
    },
    roleInAppTooltip: {
        id: 'src.Screens.Dashboard.TeamMembersTable.roleInAppTooltip',
        defaultMessage: 'Określa poziom uprawnień w panelu, np. kto może zarządzać parafią, użytkownikami i ustawieniami.',
    },
    parishFunction: {
        id: 'src.Screens.Dashboard.TeamMembersTable.parishFunction',
        defaultMessage: 'Funkcja w parafii',
    },
    parishFunctionTooltip: {
        id: 'src.Screens.Dashboard.TeamMembersTable.parishFunctionTooltip',
        defaultMessage: 'Opisuje, kim dana osoba jest w parafii, np. księdzem, lektorem, ministrantem albo inną przypisaną funkcją.',
    },
    interactions: {
        id: 'src.Screens.Dashboard.TeamMembersTable.interactions',
        defaultMessage: 'Działania',
    },
    searchPlaceholder: {
        id: 'src.Screens.Dashboard.TeamMembersTable.searchPlaceholder',
        defaultMessage: 'Szukaj po imieniu lub emailu',
    },
    itemsPerPage: {
        id: 'src.Screens.Dashboard.TeamMembersTable.itemsPerPage',
        defaultMessage: 'Elementów na stronę',
    },
    previousPage: {
        id: 'src.Screens.Dashboard.TeamMembersTable.previousPage',
        defaultMessage: 'Poprzednia strona',
    },
    nextPage: {
        id: 'src.Screens.Dashboard.TeamMembersTable.nextPage',
        defaultMessage: 'Następna strona',
    },
    itemRange: {
        id: 'src.Screens.Dashboard.TeamMembersTable.itemRange',
        defaultMessage: '{min}–{max} z {total}',
    },
    emptyState: {
        id: 'src.Screens.Dashboard.TeamMembersTable.emptyState',
        defaultMessage: 'Brak członków do wyświetlenia.',
    },
});

export default TeamMembersTable;
